import { DcRenderingGroup } from "./DcRenderingGroup";
import { Env } from "../settings/Env";
import { XRInputHelper } from "./XRInputHelper";
import { Vector3 } from "@babylonjs/core/Maths/math.vector.js";
import { Animation } from "@babylonjs/core/Animations/animation.js";
import { SceneLoader } from "@babylonjs/core/Loading/sceneLoader.js";
const meshToDay = (mesh) => Number.parseInt(mesh.name.split("_")[1]);
const pickableCubeAnimationName = "pickableCubeAnimation";
const openCubeAnimationName = "openCubeAnimation";
export class XmasCalendarView {
    get isXmasEnvironment() { return this._isXmasEnvironment; }
    set isXmasEnvironment(value) {
        this._isXmasEnvironment = value;
        this._update();
    }
    get inLobbyScene() { return this._inLobbyScene; }
    set inLobbyScene(value) {
        this._inLobbyScene = value;
        this._update();
    }
    constructor(scene, guiRootNode, lightsToExclude) {
        this._loaded = false;
        this._loaderResult = null;
        this._calendarCubes = new Map();
        this._isXmasEnvironment = false;
        this._inLobbyScene = false;
        this._scene = scene;
        this._guiRootNode = guiRootNode;
        this._lightsToExclude = lightsToExclude;
        this._today = new Date().getDate();
        this._pickableCubeAnimation = this._createPickableCubeAnimation();
        this._openCubeAnimation = this._createOpenCubeAnimation();
    }
    dispose() {
        this._disposeLoaderResult();
    }
    async preloadAsync() {
        if (this._loaded) {
            this._update();
            return;
        }
        this._loaderResult = await SceneLoader.ImportMeshAsync("", Env.xmasCalendar.sceneUrl, "", this._scene);
        const rootMesh = this._loaderResult.meshes[0];
        rootMesh.parent = this._guiRootNode;
        rootMesh.position.y = -1.23;
        const cubeMeshes = this._loaderResult.meshes.filter(m => m.name.startsWith("cube_"));
        cubeMeshes.forEach(mesh => {
            mesh.renderingGroupId = DcRenderingGroup.dialogs;
            const day = meshToDay(mesh);
            // mesh.isPickable = day === this._today;
            this._loadCalendarState();
            const isOpened = this._calendarCubes.get(day)?.isOpened ?? false;
            const cube = { day, mesh, isOpened };
            this._calendarCubes.set(day, cube);
            mesh.cube = cube;
            const clickAddon = XRInputHelper.addClickAddon(mesh);
            clickAddon.onClick.add((clickedMesh) => {
                const cube = clickedMesh.cube;
                if (cube.isOpened || cube.day > this._today)
                    return;
                this._stopPickableCubeAnimation(cube.mesh);
                this._startOpenCubeAnimation(cube.mesh);
                cube.isOpened = true;
                this._saveCalendarState();
            });
            const pointerAddon = XRInputHelper.addPointerAddon(mesh);
            pointerAddon.onPointerEnter.add((pointedMesh) => {
                const cube = pointedMesh.cube;
                if (cube.day <= this._today && !cube.isOpened) {
                    this._stopPickableCubeAnimation(pointedMesh);
                    pointedMesh.scaling = Vector3.One().scale(1.1);
                }
            });
            pointerAddon.onPointerOut.add((pointedMesh) => {
                const cube = pointedMesh.cube;
                pointedMesh.scaling = Vector3.One();
                if (cube.day === this._today && !cube.isOpened) {
                    this._startPickableCubeAnimation(pointedMesh);
                }
            });
        });
        this._loaderResult.lights.forEach(l => l.includedOnlyMeshes = this._loaderResult.meshes);
        this._lightsToExclude.forEach(l => l.excludedMeshes = l.excludedMeshes.concat(this._loaderResult.meshes));
        this._update();
        this._loaded = true;
    }
    _update() {
        const todayDate = new Date();
        const december = 11; //(debug variable)
        const calendarBeginDate = new Date(2023, december, 1);
        const calendarEndDate = new Date(2023, december, 26);
        const isVisible = this.inLobbyScene && this.isXmasEnvironment &&
            todayDate >= calendarBeginDate && todayDate < calendarEndDate;
        this._setVisibility(isVisible);
        for (const cube of this._calendarCubes.values()) {
            this._stopPickableCubeAnimation(cube.mesh);
        }
        if (todayDate >= calendarEndDate) {
            localStorage.removeItem(XmasCalendarView._xmasCalendarSettingsKey);
        }
        if (!isVisible)
            return;
        this._today = todayDate.getDate();
        this._loadCalendarState();
        for (const cube of this._calendarCubes.values()) {
            const lookAtDelta = cube.day > this._today || !cube.isOpened ? Vector3.Forward() : Vector3.Backward();
            cube.mesh.lookAt(cube.mesh.position.add(lookAtDelta));
            cube.mesh.animations = [];
            if (cube.day === this._today && cube.mesh.isVisible && !cube.isOpened) {
                // cube.mesh.isPickable = true;
                cube.mesh.animations = [this._pickableCubeAnimation];
                this._startPickableCubeAnimation(cube.mesh);
            }
        }
    }
    _setVisibility(value) {
        for (const cube of this._calendarCubes.values()) {
            cube.mesh.isVisible = value;
            // cube.mesh.isPickable = false;
            cube.mesh.isPickable = value;
        }
    }
    _startPickableCubeAnimation(mesh) {
        mesh.animations = [this._pickableCubeAnimation];
        this._scene.beginAnimation(mesh, 0, 33, true, 1);
    }
    _stopPickableCubeAnimation(mesh) {
        this._scene.stopAnimation(mesh, pickableCubeAnimationName);
    }
    _startOpenCubeAnimation(mesh) {
        mesh.animations = [this._openCubeAnimation];
        mesh.rotationQuaternion = null;
        this._scene.beginAnimation(mesh, 0, 29, false, 1);
        // this._scene.beginDirectAnimation(mesh, [this._openCubeAnimation], 0, 29, false, 1);
    }
    _disposeLoaderResult() {
        if (this._loaderResult === null)
            return;
        this._loaderResult.meshes.forEach(x => x.dispose(false, true));
        this._loaderResult.particleSystems.forEach(x => x.dispose(true));
        this._loaderResult.skeletons.forEach(x => x.dispose());
        this._loaderResult.animationGroups.forEach(x => x.dispose());
        this._loaderResult.transformNodes.forEach(x => x.dispose(false, true));
        this._loaderResult.geometries.forEach(x => x.dispose());
        this._loaderResult.lights.forEach(x => x.dispose(false, true));
    }
    _createPickableCubeAnimation() {
        const result = new Animation(pickableCubeAnimationName, "scaling", 60, Animation.ANIMATIONTYPE_VECTOR3, Animation.ANIMATIONLOOPMODE_CYCLE);
        result.setKeys([
            {
                frame: 0,
                value: new Vector3(1, 1, 1)
            },
            {
                frame: 16,
                value: new Vector3(1.1, 1.1, 1.1)
            },
            {
                frame: 33,
                value: new Vector3(1, 1, 1)
            },
        ]);
        return result;
    }
    _createOpenCubeAnimation() {
        const result = new Animation(openCubeAnimationName, "rotation.y", 60, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CYCLE);
        result.setKeys([
            {
                frame: 0,
                value: 0,
            },
            {
                frame: 29,
                value: Math.PI,
            },
        ]);
        return result;
    }
    _loadCalendarState() {
        const xmasCalendarJson = localStorage.getItem(XmasCalendarView._xmasCalendarSettingsKey);
        if (xmasCalendarJson === null)
            return;
        // const xmasCalendarJson: Nullable<string> = "[{\"day\":1,\"isOpened\":false},{\"day\":2,\"isOpened\":true},{\"day\":3,\"isOpened\":false},{\"day\":4,\"isOpened\":true},{\"day\":5,\"isOpened\":false},{\"day\":6,\"isOpened\":true},{\"day\":7,\"isOpened\":true},{\"day\":8,\"isOpened\":true},{\"day\":9,\"isOpened\":false},{\"day\":10,\"isOpened\":false},{\"day\":11,\"isOpened\":false},{\"day\":12,\"isOpened\":false},{\"day\":13,\"isOpened\":true},{\"day\":14,\"isOpened\":true},{\"day\":15,\"isOpened\":true},{\"day\":16,\"isOpened\":false},{\"day\":17,\"isOpened\":false},{\"day\":18,\"isOpened\":false},{\"day\":19,\"isOpened\":false},{\"day\":20,\"isOpened\":false},{\"day\":21,\"isOpened\":false},{\"day\":22,\"isOpened\":false},{\"day\":23,\"isOpened\":false},{\"day\":24,\"isOpened\":false},{\"day\":25,\"isOpened\":false}]";
        try {
            const storedCubes = JSON.parse(xmasCalendarJson);
            storedCubes.forEach(storedCube => {
                const cube = this._calendarCubes.get(storedCube.day);
                if (cube)
                    cube.isOpened = storedCube.isOpened;
            });
        }
        catch {
            return;
        }
    }
    _saveCalendarState() {
        const storedCubes = [];
        for (const cube of this._calendarCubes.values())
            storedCubes.push({ day: cube.day, isOpened: cube.isOpened });
        const xmasCalendarJson = JSON.stringify(storedCubes);
        localStorage.setItem(XmasCalendarView._xmasCalendarSettingsKey, xmasCalendarJson);
    }
}
XmasCalendarView._xmasCalendarSettingsKey = "xmas_calendar";
